// Organization policy layer — boundary module.
//
// An organization can impose AI provider settings (base URL, API key, model)
// and lock/hide the corresponding fields in Settings by hosting a `config.json`
// alongside the task pane bundle (same origin as the add-in, no CORS/manifest
// channel needed — see docs/superpowers/plans/2026-07-02-gap-5-governance-policy-config.md).
//
// Resolution order: imposed policy > user override (roamingSettings) > build-time
// default (config.ts). A field listed in `hidden` is implicitly locked too.
//
// Security note: a value imposed here is still visible client-side (e.g. in
// network requests) — this is a deployment convenience, not a protected secret.
// A genuinely protected secret requires proxying through an organization backend.

import { AddinSettings } from "./settings";
import { DEFAULT_APP_NAME } from "./config";
import type { SensitivityLevel } from "./data-classification";

export type PolicyField =
  | "baseUrl"
  | "apiKey"
  | "model"
  | "purviewFilterEnabled"
  | "telemetryEnabled"
  | "telemetryEndpoint"
  | "telemetryToken";

export interface OrgPolicy {
  baseUrl?: string;
  apiKey?: string;
  model?: string;
  locked?: PolicyField[];
  hidden?: PolicyField[];
  /** Deployment-wide override for the app name shown in the task pane UI (header, tab title). */
  appName?: string;
  /** Imposes the global Purview filter switch (see settings.ts, data-classification.ts). */
  purviewFilterEnabled?: boolean;
  /** Imposes the OTLP telemetry opt-in switch (see settings.ts, telemetry.ts, ADR-006). */
  telemetryEnabled?: boolean;
  /** Imposes the OTLP/HTTP traces endpoint URL telemetry is pushed to. */
  telemetryEndpoint?: string;
  /** Imposes the `Authorization` header value sent with every telemetry export.
   * Security note: same caveat as `apiKey` — a value imposed here is visible to
   * anyone who can load the task pane, not a protected secret. */
  telemetryToken?: string;
  /** Minimum sensitivity level that triggers a block. Defaults to "secret" (see
   * data-classification.ts DEFAULT_BLOCK_THRESHOLD) when unset. */
  blockThreshold?: SensitivityLevel;
  /** Tenant-specific label display name → level, merged over (and taking priority
   * over) the built-in FR/EN default mapping in data-classification.ts. */
  sensitivityLabelMap?: Record<string, SensitivityLevel>;
}

const POLICY_URL = "/config.json";

/**
 * Fetches the optional org policy from `/config.json` (same origin as the
 * add-in bundle). Degrades to an empty policy on any failure — missing file,
 * network error, or invalid JSON — so a misconfigured or absent deployment
 * never breaks the add-in.
 */
export async function loadPolicy(): Promise<OrgPolicy> {
  try {
    const response = await fetch(POLICY_URL);
    if (!response.ok) {
      return {};
    }
    const data = (await response.json()) as OrgPolicy;
    return data ?? {};
  } catch {
    return {};
  }
}

/** Overwrites settings fields imposed by the org policy; leaves the rest untouched. */
export function applyPolicy(settings: AddinSettings, policy: OrgPolicy): AddinSettings {
  return {
    ...settings,
    ...(policy.baseUrl !== undefined ? { baseUrl: policy.baseUrl } : {}),
    ...(policy.apiKey !== undefined ? { apiKey: policy.apiKey } : {}),
    ...(policy.model !== undefined ? { model: policy.model } : {}),
    ...(policy.purviewFilterEnabled !== undefined
      ? { purviewFilterEnabled: policy.purviewFilterEnabled }
      : {}),
    ...(policy.telemetryEnabled !== undefined ? { telemetryEnabled: policy.telemetryEnabled } : {}),
    ...(policy.telemetryEndpoint !== undefined
      ? { telemetryEndpoint: policy.telemetryEndpoint }
      : {}),
    ...(policy.telemetryToken !== undefined ? { telemetryToken: policy.telemetryToken } : {}),
  };
}

/** Effective locked fields: explicit `locked` plus anything in `hidden` (hidden implies locked). */
export function getLockedFields(policy: OrgPolicy): PolicyField[] {
  const locked = new Set<PolicyField>(policy.locked ?? []);
  for (const field of policy.hidden ?? []) {
    locked.add(field);
  }
  return Array.from(locked);
}

/** Fields that must not be rendered in Settings. */
export function getHiddenFields(policy: OrgPolicy): PolicyField[] {
  return policy.hidden ?? [];
}

/**
 * Resolves the app name shown in the task pane UI: `config.json`'s `appName`
 * when present, otherwise the build-time default. Reuses `loadPolicy`'s
 * fail-safe fetch, so a missing/invalid `config.json` never crashes the UI.
 */
export async function loadAppName(): Promise<string> {
  const policy = await loadPolicy();
  return policy.appName ?? DEFAULT_APP_NAME;
}

/** True when the policy imposes, locks, or hides at least one field. */
export function isPolicyActive(policy: OrgPolicy): boolean {
  return (
    policy.baseUrl !== undefined ||
    policy.apiKey !== undefined ||
    policy.model !== undefined ||
    policy.purviewFilterEnabled !== undefined ||
    policy.telemetryEnabled !== undefined ||
    policy.telemetryEndpoint !== undefined ||
    policy.telemetryToken !== undefined ||
    (policy.locked?.length ?? 0) > 0 ||
    (policy.hidden?.length ?? 0) > 0
  );
}
